import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Trash, Paperclip, CheckCircle, IdentificationCard } from "@phosphor-icons/react";

const MAX_FILES = 8;
const MAX_SIZE = 4 * 1024 * 1024; // 4MB
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

const SLOTS = [
  { category: "aadhaar", label: "Aadhaar Card", required: true, hint: "Front + back, single PDF preferred" },
  { category: "student_id", label: "Existing Student ID", required: true, hint: "Current college / school ID" },
  { category: "pan", label: "PAN Card", required: false, hint: "Optional" },
  { category: "transcript", label: "College Transcript / Marksheet", required: false, hint: "Optional · most recent" },
];

/**
 * DocumentsUpload — 4 named slots (Aadhaar / Student ID required, PAN / Transcript optional)
 * plus a free-form "Additional documents" area. All files emitted as
 * `{name, mime_type, base64, category}` so the backend can validate required types.
 */
export default function DocumentsUpload({ value = [], onChange }) {
  const extraRef = useRef(null);
  const [reading, setReading] = useState(false);

  const readFile = (f) =>
    new Promise((resolve, reject) => {
      if (!ACCEPTED.includes(f.type)) {
        toast.error(`${f.name}: file type not supported (PDF / PNG / JPG / WebP)`);
        return reject();
      }
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name}: too large (max 4MB)`);
        return reject();
      }
      const reader = new FileReader();
      reader.onload = () => resolve({ name: f.name, mime_type: f.type, base64: reader.result });
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

  // Set/replace a file for a fixed-category slot (always exactly one per slot)
  const setSlot = async (category, file) => {
    if (!file) return;
    setReading(true);
    try {
      const doc = await readFile(file);
      const next = [...value.filter((d) => (d.category || "other") !== category), { ...doc, category }];
      onChange(next);
    } catch {/* toast already shown */}
    finally { setReading(false); }
  };

  const clearSlot = (category) => onChange(value.filter((d) => (d.category || "other") !== category));

  const addExtras = async (files) => {
    const arr = Array.from(files);
    if (value.length + arr.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} documents in total`);
      return;
    }
    setReading(true);
    const next = [...value];
    for (const f of arr) {
      try {
        const doc = await readFile(f);
        next.push({ ...doc, category: "other" });
      } catch {/* ignore */}
    }
    onChange(next);
    setReading(false);
    if (extraRef.current) extraRef.current.value = "";
  };

  const removeExtra = (i) => onChange(value.filter((_, idx) => idx !== i));

  const findSlot = (category) => value.find((d) => (d.category || "other") === category);
  const others = value.filter((d) => (d.category || "other") === "other");

  return (
    <div className="space-y-5" data-testid="documents-upload">
      <div className="border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-1">
          <IdentificationCard size={16} weight="duotone" className="text-[var(--sms-primary)]" />
          <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em]">Required documents</div>
        </div>
        <div className="text-[11px] text-slate-500 mb-4">Aadhaar &amp; current Student ID are mandatory. PAN and transcripts are optional.</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {SLOTS.map((s) => {
            const doc = findSlot(s.category);
            return (
              <SlotRow
                key={s.category}
                slot={s}
                doc={doc}
                disabled={reading}
                onPick={(file) => setSlot(s.category, file)}
                onClear={() => clearSlot(s.category)}
              />
            );
          })}
        </div>
      </div>

      <div className="border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Paperclip size={16} weight="duotone" className="text-[var(--sms-primary)]" />
          <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em]">
            Additional documents <span className="text-slate-400">({others.length})</span>
          </div>
        </div>
        {others.length > 0 && (
          <ul className="mb-4 space-y-2">
            {others.map((d) => {
              const i = value.indexOf(d);
              return (
                <li key={i} data-testid={`doc-row-${i}`} className="flex items-center gap-3 px-3 py-2 border border-slate-200 bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{d.name}</div>
                    <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">{d.mime_type}</div>
                  </div>
                  <button type="button" onClick={() => removeExtra(i)} data-testid={`doc-remove-${i}`} className="text-slate-400 hover:text-[var(--sms-danger)]">
                    <Trash size={14} weight="bold" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <input
          ref={extraRef}
          type="file"
          accept=".pdf,image/png,image/jpeg,image/webp"
          multiple
          data-testid="docs-extra-input"
          onChange={(e) => addExtras(e.target.files)}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => extraRef.current?.click()}
          disabled={reading || value.length >= MAX_FILES}
          data-testid="docs-add-extra"
          className="sms-btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
        >
          <UploadSimple size={14} weight="bold" />
          {others.length === 0 ? "Add other documents" : "Add more"}
        </button>
        <div className="mt-2 text-[10px] font-mono-sms uppercase tracking-[0.15em] text-slate-400">
          PDF / PNG / JPG · Max 4 MB each · Up to {MAX_FILES} files in total
        </div>
      </div>
    </div>
  );
}

function SlotRow({ slot, doc, disabled, onPick, onClear }) {
  const inputRef = useRef(null);
  const filled = !!doc;
  return (
    <div
      data-testid={`slot-${slot.category}`}
      className={`border p-3 transition-colors ${filled ? "border-[var(--sms-success)] bg-[#F0FDF4]/40" : slot.required ? "border-slate-300" : "border-slate-200 bg-slate-50/50"}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-medium inline-flex items-center gap-1.5">
            {filled && <CheckCircle size={14} weight="duotone" className="text-[var(--sms-success)]" />}
            {slot.label}
            {slot.required && <span className="text-[var(--sms-danger)]">*</span>}
            {!slot.required && <span className="font-mono-sms text-[9px] uppercase tracking-[0.2em] text-slate-400 ml-1">Optional</span>}
          </div>
          <div className="text-[11px] text-slate-500">{slot.hint}</div>
        </div>
      </div>
      {filled ? (
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 border border-slate-200 bg-white" data-testid={`slot-${slot.category}-file`}>
          <div className="flex-1 min-w-0">
            <div className="text-xs truncate">{doc.name}</div>
            <div className="font-mono-sms text-[9px] uppercase tracking-[0.15em] text-slate-500">{doc.mime_type}</div>
          </div>
          <button type="button" onClick={() => inputRef.current?.click()} data-testid={`slot-${slot.category}-replace`} className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-primary)] hover:underline">Replace</button>
          <button type="button" onClick={onClear} data-testid={`slot-${slot.category}-clear`} className="text-slate-400 hover:text-[var(--sms-danger)]"><Trash size={12} weight="bold" /></button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          data-testid={`slot-${slot.category}-upload`}
          className="sms-btn-secondary text-xs w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <UploadSimple size={12} weight="bold" /> Upload
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/png,image/jpeg,image/webp"
        data-testid={`slot-${slot.category}-input`}
        onChange={(e) => onPick(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
}
